import { useState, useMemo, useEffect, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Spinner, Pagination, ProgressBar, Button, Form } from 'react-bootstrap';
import Badge from '../base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faRotateLeft,
  faScissors,
  faTriangleExclamation,
  faUser,
  faUserPen
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { buildHistoryRows } from '../../helpers/executionHistoryBlocks.js';
import { fingerprintExecutions } from '../../helpers/diameterFingerprint.js';
import { INFLUX_API } from '../../config/api';
import { useMachineExecutionLogs } from '../../features/execution-logs/hooks/useExecutionLogs.js';
import { http } from '../../features/jobs/api/http';
import { buildLiveDrumRows, liveDrumPercent } from '../../helpers/liveMachineDrums.js';
import SplitExecutionModal from './SplitExecutionModal.js';
import EditExecutionLogModal from '../modals/EditExecutionLogModal.js';
import RequeueExecutionModal from './RequeueExecutionModal.js';
import EditOperatorsModal from '../modals/EditOperatorsModal.js';
import ReallocationWizardModal from './ReallocationWizardModal.js';

// Phase labels. Kept identical to the live queue-edit table and ReconcileBoard,
// since toggling between them swaps one table for the other. The operator HMIs
// keep the Romanian shop-floor terms — that application is not translated.
const PHASE_LABEL = {
  unwinding: 'UNWINDING',
  pre_stranding: 'PRE-STRANDING',
  conductor_isolation: 'ISOLATION',
  rope: 'ROPE',
  interior_sheat: 'INNER SHEATH',
  armouring: 'ARMOURING',
  exterior_sheat: 'OUTER SHEATH',
  coiling: 'COILING',
  rewinding: 'REWINDING'
};
const phaseLabel = p => PHASE_LABEL[p] || p || '-';

const COLOR_HEX = {
  BLUE: '#2f7cff',
  BROWN: '#8b5a2b',
  BLACK: '#222222',
  GREY: '#7a7a7a',
  GRAY: '#7a7a7a',
  'GREEN-YELLOW': '#9acd32',
  GREEN: '#2e8b57',
  YELLOW: '#f0c400',
  RED: '#d9534f',
  WHITE: '#e5e5e5',
  ORANGE: '#f39c12'
};
const colorHex = c => COLOR_HEX[String(c || '').toUpperCase()] || null;

const fmt2 = v => (v == null ? '-' : Number(v).toFixed(2));

// Copper line from an ExecutionLog materialExecution entry (uses `diameter`).
function copperLine(materialExecution) {
  const mp = Array.isArray(materialExecution) ? materialExecution : [];
  const cop = mp.find(m => String(m?.material || '').toUpperCase() === 'COPPER');
  if (!cop) return null;
  const d = cop.diameter ?? cop.wireDiameter;
  const type = String(cop.wireType || '').toUpperCase();
  if (d == null) return 'Copper';
  if (type === 'SINGLE-WIRE') return `Cu Ø ${fmt2(d)} mm`;
  if (cop.numberOfWires && d) return `${cop.numberOfWires} x ${fmt2(d)} mm`;
  return `Cu Ø ${fmt2(d)} mm`;
}

// Duration in a compact human form — segments run from seconds to whole shifts.
const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (!s) return '0m';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

const hhmm = ts => (ts ? dayjs(Number(ts)).format('HH:mm') : '-');

// Stable colour per operator name so the same person keeps the same badge colour
// across rows — makes a handover legible at a glance.
const operatorHue = name => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

// A drum can be produced across a handover, so a log carries one span per
// operator. `operators` is empty on anything produced before segment tracking —
// fall back to the single scalar so old rows still show who was there.
const operatorSpans = log => {
  const segs = Array.isArray(log?.operators) ? log.operators.filter(Boolean) : [];
  if (segs.length) return segs;
  if (log?.operator) {
    return [{ operator: log.operator, shift: log.shift || null, legacy: true }];
  }
  return [];
};

const OperatorBadge = ({ name, title }) => {
  const hue = operatorHue(name);
  return (
    <span
      title={title}
      className="fs-10 px-2 py-1 rounded-pill text-nowrap"
      style={{
        background: `hsl(${hue}, 60%, 92%)`,
        color: `hsl(${hue}, 70%, 28%)`,
        border: `1px solid hsl(${hue}, 55%, 78%)`,
        lineHeight: 1.1
      }}
    >
      {name || 'unassigned'}
    </span>
  );
};

const LIMIT = 100;

// Per-machine execution history, navigated by DAY (◀ [date] ▶). Rendered in the
// SAME form as the live queue-edit table (same columns / phoenix-table styling) so
// toggling swaps one for the other seamlessly. Newest-first (backend sorts
// -completedAt, _id). Bounded to one day (paginates within busy days). Row click →
// EditExecutionLogModal (period + machine data).
const MachineExecutionHistoryPanel = ({ machineId }) => {
  const [day, setDay] = useState(() => dayjs().startOf('day'));
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  // execution ids whose per-operator breakdown is open
  const [expanded, setExpanded] = useState(() => new Set());
  // token whose operator attribution is being corrected
  const [editOperatorsFor, setEditOperatorsFor] = useState(null);
  const [requeueLog, setRequeueLog] = useState(null);
  const [showRealloc, setShowRealloc] = useState(false);
  // The products picked for a repair — kept as a Map so a selection made on
  // Tuesday still knows its own name on Wednesday.
  //
  // It used to be a Set of ids cleared on every day change, which made the one
  // thing it is for impossible: a run that slipped rarely stops at midnight, so
  // the products that have to be repaired together are almost never all on the
  // same day. Each entry also remembers the span of days it has been seen on,
  // which is what the drum ledger opens on.
  const [selectedJobs, setSelectedJobs] = useState(() => new Map());
  const navigate = useNavigate();

  const toggleJob = (jobId, log) =>
    setSelectedJobs(prev => {
      const next = new Map(prev);
      if (next.has(jobId)) next.delete(jobId);
      else
        next.set(jobId, {
          label: log?.productLabel || jobId,
          jobCode: log?.jobCode || null,
          firstTs: Number(log?.startedAt) || Number(log?.completedAt) || null,
          lastTs: Number(log?.completedAt) || null
        });
      return next;
    });

  const isToday = day.isSame(dayjs(), 'day');

  const filters = useMemo(
    () => ({
      dateFrom: day.startOf('day').valueOf(),
      dateTo: day.endOf('day').valueOf(),
      page,
      limit: LIMIT
    }),
    [day, page]
  );

  const { data, isLoading, error, refetch } = useMachineExecutionLogs(machineId, filters);
  // Memoised: four hooks below depend on it, and `|| []` is a new array
  // identity on every render.
  const logs = useMemo(() => data?.data || [], [data]);
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  // The selection deliberately survives — see `selectedJobs`.
  const shiftDay = dir => {
    setDay(d => d.add(dir, 'day'));
    setPage(1);
  };

  // A selected product seen again on another day widens the span the drum
  // ledger will open on. Days nobody visited are not guessed at.
  useEffect(() => {
    if (!logs.length) return;
    setSelectedJobs(prev => {
      let touched = false;
      const next = new Map(prev);
      for (const log of logs) {
        const entry = next.get(String(log.jobId));
        if (!entry) continue;
        const from = Number(log.startedAt) || Number(log.completedAt);
        const to = Number(log.completedAt);
        const firstTs = entry.firstTs == null ? from : Math.min(entry.firstTs, from);
        const lastTs = entry.lastTs == null ? to : Math.max(entry.lastTs, to);
        if (firstTs !== entry.firstTs || lastTs !== entry.lastTs) {
          next.set(String(log.jobId), { ...entry, firstTs, lastTs });
          touched = true;
        }
      }
      return touched ? next : prev;
    });
  }, [logs]);

  // One entry per DRUM, in the order the machine ran them (newest first), with
  // the two halves of a merge execution pulled together. Grouping by job — what
  // this used to do — scattered one physical drum across the table, which is how
  // a merge-execution product came to be recorded with 4 m on two conductors:
  // nothing on screen said the 10 300 m row and the 4 m row were the same bobbin.
  const drums = useMemo(() => buildHistoryRows(logs), [logs]);

  // ── what the machine was SET to make, while each drum was on it ───────────
  //
  // The production records cannot show a drum change the machine never saw:
  // which product a drum holds is whatever the queue said at the time. The
  // diameter setpoint can, because it changes when the operator changes
  // product. Read straight out of Influx for the day on screen, so it works on
  // every execution ever recorded and nothing new has to be written first.
  const [profile, setProfile] = useState([]);
  const [splitFor, setSplitFor] = useState(null);

  // ── what is on the machine RIGHT NOW ──────────────────────────────────────
  //
  // This table is built from ExecutionLogs, and an ExecutionLog is written when
  // a drum FINISHES. So the run it shows stops at the last completed drum, and
  // the product actually turning on the line is absent — which is very often
  // the product a mis-recorded drum belongs to. You could see the evidence and
  // not the thing it pointed at: the run had a beginning but no end.
  //
  // Off by default, because these rows are not history. They are the present:
  // they change under you, and the MACHINE owns their lengths —
  // `unifiedWorker` assigns `producedLength` from the PLC counter on every loop,
  // so anything written here is overwritten within a second. They are context
  // and a DESTINATION, never a target. That is why they carry no actions.
  const [showLive, setShowLive] = useState(false);
  const [live, setLive] = useState([]);

  useEffect(() => {
    if (!showLive || !machineId) {
      setLive([]);
      return undefined;
    }
    let alive = true;
    const read = async () => {
      try {
        const { data: q } = await http.get(`/api/redis/queues/${machineId}`);
        if (!alive) return;
        setLive(buildLiveDrumRows(q));
      } catch {
        // No queue is no live rows — the table works exactly as before.
        if (alive) setLive([]);
      }
    };
    read();
    const timer = setInterval(read, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [showLive, machineId]);

  useEffect(() => {
    let alive = true;
    setProfile([]);
    if (!machineId || !logs.length) return undefined;
    (async () => {
      try {
        const start = new Date(day.startOf('day').valueOf()).toISOString();
        const end = new Date(day.endOf('day').valueOf()).toISOString();
        const res = await fetch(
          `${INFLUX_API}/api/influx/${machineId}/diameter-profile` +
            `?start=${start}&end=${end}&bucket=machine_data&every=1m`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setProfile(Array.isArray(data?.samples) ? data.samples : []);
      } catch {
        // No profile is no suggestion — the table works exactly as before.
      }
    })();
    return () => {
      alive = false;
    };
  }, [machineId, day, logs.length]);

  const fingerprint = useMemo(
    () => (profile.length ? fingerprintExecutions(logs, profile) : new Map()),
    [logs, profile]
  );

  // distinct products in view — used to open the Reconcile wizard for them
  const historyJobIds = useMemo(
    () => [...new Set((logs || []).map(l => String(l.jobId)).filter(Boolean))],
    [logs]
  );
  const openReconcile = () =>
    navigate('/apps/project-management/reconcile-wizard', {
      state: {
        jobIds: selectedJobs.size ? [...selectedJobs.keys()] : historyJobIds,
        machineId,
        returnTo: `/apps/machine-management/machine-queue/${machineId}/edit`
      }
    });

  // The days the selection actually covers, so the ledger opens on the run that
  // matters instead of an arbitrary window. Falls back to the day on screen.
  const selectionSpan = useMemo(() => {
    const stamps = [...selectedJobs.values()].flatMap(v =>
      [v.firstTs, v.lastTs].filter(Boolean)
    );
    // Nothing picked: the day on screen plus the one on either side, because a
    // run that slipped rarely stops at midnight and a chain cut at one is a
    // chain with a wrong first boundary.
    if (!stamps.length) {
      return { from: day.subtract(1, 'day').startOf('day'), to: day.add(1, 'day').endOf('day') };
    }
    return {
      from: dayjs(Math.min(...stamps)).startOf('day'),
      to: dayjs(Math.max(...stamps)).endOf('day')
    };
  }, [selectedJobs, day]);

  // The ledger shows the machine's WHOLE run for the range, selected or not:
  // the drums are boundaries on one continuous ruler, and hiding the ones
  // between two selected products would make the arithmetic of a shift wrong.
  // The selection sets the range and lights the rows up.
  const openLedger = () =>
    navigate(`/apps/machine-management/drum-ledger/${machineId}`, {
      state: {
        jobIds: [...selectedJobs.keys()],
        from: selectionSpan.from.format('YYYY-MM-DD'),
        to: selectionSpan.to.format('YYYY-MM-DD'),
        returnTo: `/apps/machine-management/machine-queue/${machineId}/edit`
      }
    });

  // Products that are selected but produced nothing on the day on screen. The
  // Reallocate board builds its "belongs to product" list from the drums in
  // view, so without these a drum cannot be moved to a product from another day
  // — which is most of them, once a run has slipped.
  const extraProducts = useMemo(
    () =>
      [...selectedJobs.entries()]
        .filter(([jobId]) => !historyJobIds.includes(jobId))
        .map(([jobId, v]) => ({ jobId, productLabel: v.label })),
    [selectedJobs, historyJobIds]
  );

  return (
    <div>
      {/* Day navigator (centered): title left · nav center · spacer right */}
      <div className="d-flex align-items-center flex-wrap gap-3 py-3">
        <div style={{ flex: 1, minWidth: 200 }}>
          <h4 className="mb-0">Execution History</h4>
          <div className="fs-9 text-700">
            <b>{(machineId || '').toUpperCase()}</b> — completed executions, real
            order (newest first)
          </div>
          {/* The run's missing end. A drum that has not finished has no
              ExecutionLog, so the product on the line cannot be seen here — and
              it is usually the one a mis-recorded drum belongs to. */}
          <Form.Check
            type="switch"
            id="exec-history-live"
            className="fs-10 mt-1"
            checked={showLive}
            onChange={e => setShowLive(e.target.checked)}
            label={
              live.length
                ? `Bring live data — ${live.length} drum(s) on the machine now`
                : 'Bring live data — what is on the machine now'
            }
          />
        </div>

        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-link p-0"
            aria-label="Previous day"
            onClick={() => shiftDay(-1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              minWidth: 230,
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}
          >
            {day.format('dddd, D MMMM YYYY')}
          </span>
          <button
            className="btn btn-link p-0"
            aria-label="Next day"
            disabled={isToday}
            onClick={() => shiftDay(1)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        <div style={{ flex: 1 }} className="d-flex justify-content-end gap-2">
          {/* A drum change the machine never registered puts every later
              boundary in the wrong place, and the run that broke rarely stops
              at midnight. This day is the wrong shape for that — the ledger is
              the same drums across whatever range you choose. */}
          <Button
            size="sm"
            variant="outline-warning"
            onClick={openLedger}
            title="The whole run, across days — move the boundaries between drums"
          >
            Drum ledger{selectedJobs.size ? ` (${selectedJobs.size})` : ''}
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={!logs.length}
            onClick={() => setShowRealloc(true)}
            title="Quick swap / re-attribute produced drums (modal)"
          >
            Reallocate
          </Button>
          <Button
            size="sm"
            variant="outline-info"
            disabled={historyJobIds.length < 1 && !selectedJobs.size}
            onClick={openReconcile}
            title="Open the Reconcile wizard for the selected products (or all in view)"
          >
            Reconcile Wizard{selectedJobs.size ? ` (${selectedJobs.size})` : ''}
          </Button>
        </div>
      </div>

      {/* What is picked, and from where. Without this the selection is invisible
          the moment you change day — which is exactly when it matters, because
          the products that have to be repaired together are rarely all on one
          day. */}
      {selectedJobs.size > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2 px-3 py-2 mb-2 rounded-2 bg-body-tertiary">
          <span className="fs-10 fw-semibold text-900">
            {selectedJobs.size} selected
          </span>
          {[...selectedJobs.entries()].map(([jobId, v]) => {
            const here = historyJobIds.includes(jobId);
            return (
              <span
                key={jobId}
                className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fs-10"
                style={{
                  background: 'var(--phoenix-body-bg)',
                  border: `1px solid ${
                    here ? 'var(--phoenix-info)' : 'var(--phoenix-border-color)'
                  }`
                }}
                title={
                  here
                    ? 'produced on the day on screen'
                    : `last seen ${v.lastTs ? dayjs(v.lastTs).format('DD MMM HH:mm') : 'on another day'}`
                }
              >
                {v.label}
                {!here && v.lastTs ? (
                  <span className="text-700">· {dayjs(v.lastTs).format('DD MMM')}</span>
                ) : null}
                <Button
                  variant="link"
                  className="p-0 ms-1 fs-10 text-700"
                  style={{ lineHeight: 1 }}
                  onClick={() => toggleJob(jobId)}
                  title="Remove from the selection"
                >
                  ×
                </Button>
              </span>
            );
          })}
          <span className="fs-10 text-700 ms-auto">
            {selectionSpan.from.format('DD MMM')} – {selectionSpan.to.format('DD MMM')}
          </span>
          <Button
            size="sm"
            variant="link"
            className="p-0 fs-10"
            onClick={() => setSelectedJobs(new Map())}
          >
            clear
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : error ? (
        <div className="p-3 text-danger fs-9">{String(error?.message || error)}</div>
      ) : logs.length === 0 && !(showLive && live.length) ? (
        // "Nothing finished today" is not "nothing happened today" — a drum that
        // has been turning since yesterday finishes nothing and is the whole
        // reason the toggle exists.
        <div className="text-center py-5 text-700 fs-9">
          No executions on this day.
          {!showLive && (
            <div className="fs-10 mt-1">
              Turn on <b>Bring live data</b> to see what is on the machine now.
            </div>
          )}
        </div>
      ) : (
        <>
          <Table
            responsive
            hover
            className="phoenix-table border-top border-200 fs-9 mb-0"
          >
            <thead>
              <tr className="text-center">
                <th style={{ width: '9%' }}>No. / Date</th>
                <th style={{ width: '16%' }}>Job</th>
                <th style={{ width: '12%' }}>Phase</th>
                <th>Input</th>
                <th>In progress</th>
                <th>Executed</th>
                <th>Drum</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* ── on the machine now ──
                  Above everything, because the table is newest-first and these
                  are newer than any finished drum. Marked, and deliberately
                  actionless: the machine is writing their lengths, so there is
                  nothing here that could be corrected without being overwritten
                  a second later. What they are for is being SEEN — the run
                  finally has an end — and being SELECTED, so the product on the
                  line can be named as the place a mis-recorded drum belongs. */}
              {showLive && live.length > 0 && (
                <>
                  <tr>
                    <td
                      colSpan={9}
                      className="py-1 px-3 bg-body-tertiary"
                      style={{ borderTop: '2px solid var(--phoenix-primary)' }}
                    >
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Badge variant="phoenix" bg="primary" className="fs-10">
                          on the machine now
                        </Badge>
                        <span className="fs-10 text-700">
                          Not history — the line owns these lengths, so they cannot
                          be re-stated. The products can still <b>receive</b> a drum:
                          that is what ✂ on a row below is for.
                        </span>
                      </div>
                    </td>
                  </tr>

                  {live.map(row => {
                    const dot = colorHex(row.color);
                    const pctLive = liveDrumPercent(row);
                    return (
                      <tr
                        key={row.key}
                        style={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderLeft: `4px solid var(--phoenix-primary)`
                        }}
                      >
                        <td>
                          <div className="fw-semibold text-primary">now</div>
                          <div className="fs-10 text-700">
                            {row.status === 'ACTIVE' ? 'running' : 'queued'}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Form.Check
                              type="checkbox"
                              className="m-0"
                              checked={selectedJobs.has(String(row.jobId))}
                              onChange={() =>
                                toggleJob(String(row.jobId), {
                                  productLabel: row.productLabel,
                                  jobCode: row.jobCode
                                })
                              }
                              title="Select this product — it becomes available as the destination for a drum that was credited to the wrong product"
                            />
                            <div>
                              <Link
                                to={`/apps/project-management/project-details/${row.jobId}`}
                                className="fw-semibold text-decoration-none"
                              >
                                {row.productLabel}
                              </Link>
                              <div className="fs-10 text-700">{row.jobCode || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge variant="phoenix" bg="secondary" className="fs-10">
                            {phaseLabel(row.phase)}
                          </Badge>
                        </td>
                        <td className="fs-10 text-700">-</td>
                        <td style={{ minWidth: 120 }}>
                          <div className="fs-10 font-monospace">
                            {fmt2(row.producedLength)}/{fmt2(row.plannedLength)} m
                          </div>
                          <ProgressBar
                            now={pctLive}
                            variant="primary"
                            style={{ height: 6, borderRadius: 999 }}
                          />
                        </td>
                        <td className="fs-10 text-700">—</td>
                        <td className="fs-10 text-800">
                          {row.drum_material || '-'}
                          {row.drum_diameter ? ` Ø${row.drum_diameter}` : ''}
                          {dot ? (
                            <span
                              style={{
                                marginLeft: 6,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: dot,
                                display: 'inline-block'
                              }}
                            />
                          ) : null}
                        </td>
                        <td>
                          <Badge
                            variant="phoenix"
                            bg={row.status === 'ACTIVE' ? 'primary' : 'secondary'}
                          >
                            {row.status === 'ACTIVE' ? 'LIVE' : 'QUEUED'}
                          </Badge>
                        </td>
                        {/* Not a dead end. The DRUM's length is the line's to
                            write, but the PRODUCT is a destination: it is where
                            a drum credited to the wrong product gets sent. */}
                        <td className="fs-10" style={{ maxWidth: 200 }}>
                          <div className="text-info fw-semibold">
                            can receive a drum
                          </div>
                          <div className="text-700">
                            Its own length is the line&apos;s to write. Use ✂ on the
                            row that holds its metres and name this product there.
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}

              {drums.map(drum => (
                <Fragment key={drum.id}>
                  {/* A group-by launch — same colour or same wire Ø, queued
                      together so the machine does not change over between them.
                      Not a merge: each drum here is its own bobbin. */}
                  {drum.opensRun && drum.groupBy ? (
                    <tr>
                      <td colSpan={9} className="py-1 px-3 bg-body-tertiary">
                        <div className="d-flex align-items-center gap-2">
                          {drum.groupBy.kind === 'color' ? (
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: 2,
                                display: 'inline-block',
                                border: '1px solid rgba(255,255,255,0.25)',
                                background: colorHex(drum.groupBy.value) || '#8892a0'
                              }}
                            />
                          ) : null}
                          <span className="fw-bold fs-9 text-900">
                            {drum.groupBy.kind === 'color'
                              ? drum.groupBy.value
                              : `Ø${drum.groupBy.value}`}
                          </span>
                          <Badge variant="phoenix" bg="info" className="fs-10">
                            grouped run · {drum.runSize}{' '}
                            {drum.runSize === 1 ? 'drum' : 'drums'}
                          </Badge>
                        </div>
                      </td>
                    </tr>
                  ) : null}

                  {/* One drum, several products. The header carries what no
                      single row can: the length the drum was SUPPOSED to hold. */}
                  {drum.isMerge ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-1 px-3 bg-body-tertiary"
                        style={{
                          borderLeft: `4px solid ${drum.borderColor}`,
                          // `--phoenix-warning` is a variable this theme really
                          // defines; the `-bg-subtle` suffixes Bootstrap adds are
                          // not all carried over, and one that does not exist
                          // fails silently as no colour at all.
                          borderTop: '2px solid var(--phoenix-warning)'
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Badge variant="phoenix" bg="warning" className="fs-10">
                            merge execution · {drum.products.length} products on
                            one drum
                          </Badge>
                          <span className="fs-10 text-900 fw-semibold">
                            {drum.products.map(p => p.productLabel || '-').join(' + ')}
                          </span>
                          <span className="fs-10 text-700 font-monospace">
                            {drum.products
                              .map(p => `${fmt2(p.plannedLength)} m`)
                              .join(' + ')}
                          </span>
                          <span className="fs-10 text-700">
                            → drum {fmt2(drum.producedTotal)} /{' '}
                            {fmt2(drum.plannedTotal)} m
                          </span>
                          {drum.producedTotal < drum.plannedTotal * 0.95 ? (
                            <Badge variant="phoenix" bg="danger" className="fs-10">
                              short — taken off {fmt2(drum.plannedTotal - drum.producedTotal)} m early
                            </Badge>
                          ) : null}
                          <span className="fs-10 text-600">{drum.tokenId}</span>
                        </div>
                      </td>
                    </tr>
                  ) : null}

                  {drum.logs.map(log => {
                    const showJob = drum.showJob;
                    const border = drum.borderColor;
                    const idx = drum.index;
                    const dot = colorHex(log.color);
                    const cu = copperLine(log.materialExecution);
                    const pctDone = log.plannedLength
                      ? Math.min(
                          100,
                          Math.round((log.producedLength / log.plannedLength) * 100)
                        )
                      : 100;
                    const spans = operatorSpans(log);
                    const isOpen = expanded.has(log._id);
                    const hasBreakdown = spans.length > 1 || (spans.length === 1 && !spans[0].legacy);
                    const hint = fingerprint.get(String(log._id))?.suggestion || null;
                    return (
                      <Fragment key={log._id}>
                      <tr
                        style={{
                          cursor: 'pointer',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderLeft: `4px solid ${border}`
                        }}
                        onClick={() => setSelected(log)}
                        title="Execution details (period + machine)"
                      >
                        <td>
                          {/* Numbered by DRUM. Two rows carrying the same number
                              are one pass of the machine — which is the whole of
                              "merge execution" said without the jargon. */}
                          <div className="fw-semibold">
                            {(page - 1) * LIMIT + idx + 1}
                          </div>
                          <div className="fs-10 text-700">
                            {log.completedAt
                              ? dayjs(log.completedAt).format('DD MMM, HH:mm')
                              : '-'}
                          </div>
                        </td>
                        <td>
                          {showJob ? (
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <Form.Check
                                type="checkbox"
                                className="m-0"
                                checked={selectedJobs.has(String(log.jobId))}
                                onClick={e => e.stopPropagation()}
                                onChange={() => toggleJob(String(log.jobId), log)}
                                title="Select for the Reconcile wizard or the Drum ledger — the selection survives changing day"
                              />
                              <div>
                                <Link
                                  to={`/apps/project-management/project-details/${log.jobId}`}
                                  className="fw-semibold text-decoration-none"
                                  onClick={e => e.stopPropagation()}
                                >
                                  {log.productLabel || '-'}
                                </Link>
                                <div className="fs-10 text-700">
                                  {log.jobCode || `JOB-${(log.jobId || '').slice(0, 6)}`}
                                </div>
                                {/* Said on the row too, not only in the drum's
                                    header: a row read on its own must not look
                                    like a product that simply came up short. */}
                                {drum.isMerge ? (
                                  <div
                                    className="fs-10 text-warning"
                                    title={`One drum, one pass, shared with: ${drum.products
                                      .filter(p => p.jobId !== String(log.jobId))
                                      .map(p => p.productLabel || p.jobId)
                                      .join(', ')}`}
                                  >
                                    merge ·{' '}
                                    {drum.products
                                      .filter(p => p.jobId !== String(log.jobId))
                                      .map(p => p.productLabel || '-')
                                      .join(', ')}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <span className="fw-medium text-900">
                              {phaseLabel(log.phase)}
                            </span>
                            {dot ? (
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  background: dot,
                                  display: 'inline-block'
                                }}
                              />
                            ) : null}
                          </div>
                          {/* Who produced this drum. Several badges = a handover
                              mid-drum; the chevron opens the per-operator split. */}
                          <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap mt-1">
                            {spans.length ? (
                              <>
                                {spans.slice(0, 2).map((s, i) => (
                                  <OperatorBadge
                                    key={`${s.operator || 'x'}-${i}`}
                                    name={s.operator}
                                    title={
                                      s.legacy
                                        ? 'No per-operator breakdown (older execution)'
                                        : `${s.operator || 'unassigned'} · ${hhmm(s.startedAt)}–${hhmm(s.endedAt)} · ${fmt2(s.meters)} m`
                                    }
                                  />
                                ))}
                                {spans.length > 2 ? (
                                  <span className="fs-10 text-700">+{spans.length - 2}</span>
                                ) : null}
                                {hasBreakdown ? (
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 ms-1 text-700"
                                    style={{ lineHeight: 1 }}
                                    title={isOpen ? 'Hide operators' : 'Who worked on this drum'}
                                    onClick={e => {
                                      e.stopPropagation();
                                      setExpanded(prev => {
                                        const next = new Set(prev);
                                        if (next.has(log._id)) next.delete(log._id);
                                        else next.add(log._id);
                                        return next;
                                      });
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={isOpen ? faChevronUp : faChevronDown}
                                      className="fs-10"
                                    />
                                  </Button>
                                ) : null}
                              </>
                            ) : (
                              // Nobody was logged in. This is the case the
                              // operator edit exists FOR, and it used to be the
                              // one case with no way in — the edit lived inside
                              // a panel only openable when there was already a
                              // breakdown to show.
                              <span
                                className="fs-10 px-2 py-1 rounded-pill text-nowrap"
                                style={{
                                  border: '1px dashed var(--phoenix-border-color)',
                                  color: 'var(--phoenix-warning)'
                                }}
                              >
                                unassigned
                                {log.shift ? ` · ${log.shift}` : ''}
                              </span>
                            )}

                            {/* Always offered, spans or none. The backend
                                already serves an unassigned whole-drum span
                                when a drum has none, and accepts the first
                                assignment — only the way in was missing. */}
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 ms-1 text-700"
                              style={{ lineHeight: 1 }}
                              title="Set or correct who produced this drum"
                              onClick={e => {
                                e.stopPropagation();
                                setEditOperatorsFor(log.tokenId);
                              }}
                            >
                              <FontAwesomeIcon icon={faUserPen} className="fs-10" />
                            </Button>
                          </div>
                        </td>
                        <td className="fs-10 text-800 fw-normal">{cu || '-'}</td>
                        <td className="font-monospace fs-10 text-800 fw-normal">
                          {log.plannedLength != null
                            ? `1 x ${fmt2(log.plannedLength)} m`
                            : '-'}
                        </td>
                        <td style={{ minWidth: 120 }}>
                          <div className="font-monospace fs-10 text-800 fw-normal">
                            {fmt2(log.producedLength)}/{fmt2(log.plannedLength)} m
                          </div>
                          <ProgressBar
                            now={pctDone}
                            style={{ height: 6 }}
                            className="mt-2 bg-body-tertiary"
                          />
                        </td>
                        <td className="fs-10 text-800 fw-normal">
                          {log.drum_material
                            ? `${log.drum_material}${
                                log.drum_diameter ? ' Ø' + log.drum_diameter : ''
                              }`
                            : '-'}
                        </td>
                        <td>
                          {log.cancelled ? (
                            <Badge variant="phoenix" bg="warning">
                              CORRECTED
                            </Badge>
                          ) : (
                            <Badge variant="phoenix" bg="primary">
                              DONE
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              title="Re-queue this execution as PENDING"
                              disabled={log.cancelled}
                              style={{ width: 34, height: 34, padding: 0 }}
                              onClick={e => {
                                e.stopPropagation();
                                setRequeueLog(log);
                              }}
                            >
                              <FontAwesomeIcon icon={faRotateLeft} />
                            </Button>
                            {/* Offered on every execution, not only the flagged
                                ones: the setpoint catches a product change, but
                                a drum swapped for the same product leaves no
                                trace at all and the operator may still know. */}
                            <Button
                              size="sm"
                              variant={
                                hint?.kind === 'CHANGED_MID_DRUM'
                                  ? 'warning'
                                  : 'outline-secondary'
                              }
                              title="This was two drums — cut it, and send the rest either to whatever ran next or to a product of its own (one that is running or queued counts)"
                              disabled={log.cancelled}
                              style={{ width: 34, height: 34, padding: 0 }}
                              onClick={e => {
                                e.stopPropagation();
                                setSplitFor({ log, suggestion: hint });
                              }}
                            >
                              <FontAwesomeIcon icon={faScissors} />
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {/* What the machine was set to make while this drum was on
                          it. A suggestion and nothing more — only the operator
                          knows what was physically there, and a system that
                          decided for them would turn one wrong drum into ten. */}
                      {hint ? (
                        <tr style={{ borderLeft: `4px solid ${border}` }}>
                          <td colSpan={9} className="py-1 px-3 bg-body-tertiary">
                            <div className="d-flex align-items-start gap-2 fs-10">
                              <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="text-warning mt-1"
                              />
                              {hint.kind === 'CHANGED_MID_DRUM' ? (
                                <div>
                                  <b>Probabil aici s-a greșit.</b> The diameter
                                  setpoint moved from <b>Ø{hint.fromDiameter}</b> to{' '}
                                  <b>Ø{hint.toDiameter}</b> at{' '}
                                  <b>{hhmm(hint.splitAt)}</b> — from there the line
                                  was making another product, about{' '}
                                  <b>{hint.metresAfterEstimate.toLocaleString()} m</b>{' '}
                                  of it. This looks like a drum change the machine
                                  never registered.
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 ms-2 fs-10"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setSplitFor({ log, suggestion: hint });
                                    }}
                                  >
                                    split it here
                                  </Button>
                                </div>
                              ) : (
                                <div>
                                  This drum was made at <b>Ø{hint.dominant}</b>, but{' '}
                                  {hint.agreeing} of this product&apos;s{' '}
                                  {hint.total} drums were made at{' '}
                                  <b>Ø{hint.expected}</b> — it may belong to another
                                  product entirely.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null}

                      {isOpen ? (
                        <tr style={{ borderLeft: `4px solid ${border}` }}>
                          <td colSpan={9} className="bg-body-tertiary py-2 px-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <FontAwesomeIcon icon={faUser} className="fs-10 text-700" />
                              <span className="fs-10 fw-semibold text-900">
                                Operators on this drum
                              </span>
                              <span className="fs-10 text-700">
                                · {fmt2(log.producedLength)} m in total
                              </span>
                              {/* Fixing a drum nobody logged in for: names only, the
                                  measured time and metres stay as they are. */}
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 fs-10"
                                onClick={e => {
                                  e.stopPropagation();
                                  setEditOperatorsFor(log.tokenId);
                                }}
                                title="Correct who produced this drum"
                              >
                                <FontAwesomeIcon icon={faUserPen} className="me-1" />
                                edit
                              </Button>
                            </div>
                            <Table
                              size="sm"
                              className="fs-10 mb-0 bg-transparent"
                              style={{ maxWidth: 720 }}
                            >
                              <thead>
                                <tr className="text-700">
                                  <th className="fw-medium">Operator</th>
                                  <th className="fw-medium">Shift</th>
                                  <th className="fw-medium">From</th>
                                  <th className="fw-medium">To</th>
                                  <th className="fw-medium text-end">Running time</th>
                                  <th className="fw-medium text-end">Metres</th>
                                  <th className="fw-medium text-end">Speed</th>
                                </tr>
                              </thead>
                              <tbody>
                                {spans.map((s, i) => {
                                  // metres over the time the line actually moved for
                                  // this person — not the wall clock of their span
                                  const spd =
                                    s.runningSec > 0 && s.meters > 0
                                      ? (s.meters / (s.runningSec / 60)).toFixed(1)
                                      : null;
                                  return (
                                    <tr key={`${log._id}-seg-${i}`}>
                                      <td>
                                        <OperatorBadge name={s.operator} />
                                      </td>
                                      <td className="text-800">{s.shift || '-'}</td>
                                      <td className="font-monospace text-800">{hhmm(s.startedAt)}</td>
                                      <td className="font-monospace text-800">{hhmm(s.endedAt)}</td>
                                      <td className="font-monospace text-800 text-end">
                                        {s.runningSec != null ? fmtDur(s.runningSec) : '-'}
                                      </td>
                                      <td className="font-monospace text-800 text-end">
                                        {s.meters != null ? `${fmt2(s.meters)} m` : '-'}
                                      </td>
                                      <td className="font-monospace text-800 text-end">
                                        {spd ? `${spd} m/min` : '-'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      ) : null}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center px-3 py-2">
            <span className="text-700 fs-9">{total} {total === 1 ? 'execution' : 'executions'} on this day</span>
            {pages > 1 && (
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                />
                <Pagination.Item active>
                  {page} / {pages}
                </Pagination.Item>
                <Pagination.Next
                  disabled={page >= pages}
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                />
              </Pagination>
            )}
          </div>
        </>
      )}

      <EditExecutionLogModal
        show={!!selected}
        onHide={() => setSelected(null)}
        log={selected}
      />

      <EditOperatorsModal
        show={!!editOperatorsFor}
        tokenId={editOperatorsFor}
        onHide={() => {
          setEditOperatorsFor(null);
          refetch();
        }}
      />

      <SplitExecutionModal
        show={!!splitFor}
        log={splitFor?.log || null}
        suggestion={splitFor?.suggestion || null}
        machineId={machineId}
        onHide={() => setSplitFor(null)}
        onDone={() => {
          setSplitFor(null);
          refetch();
        }}
      />

      <RequeueExecutionModal
        show={!!requeueLog}
        log={requeueLog}
        machineId={machineId}
        onHide={() => setRequeueLog(null)}
        onDone={() => {
          setRequeueLog(null);
          refetch();
        }}
      />

      <ReallocationWizardModal
        show={showRealloc}
        logs={logs}
        extraProducts={extraProducts}
        machineId={machineId}
        onHide={() => setShowRealloc(false)}
        onDone={() => {
          setShowRealloc(false);
          refetch();
        }}
      />
    </div>
  );
};

export default MachineExecutionHistoryPanel;
